// FUNCTIONALITY-115A / CUSTOMER-R4.6 / R4.7-A / R4.9 / R4.15 — compact receipt sheet or inline form.
// RECEIPT_JPG / RECEIPT_PDF — Telegram Android WebView: accept MIME+ext, capture only for photo path, cancel-safe.

import { useEffect, useId, useRef, useState } from "react";

import type { StoreV2Lang } from "./store-v2-locale.js";
import { validatePaymentComment } from "./payment-link-security-v2.js";
import { emitEvent } from "./events-v2.js";

export const RECEIPT_ACCEPT = "image/jpeg,image/png,image/webp,application/pdf";
/** Combined picker — broad enough for Telegram Android WebView file chooser. */
export const RECEIPT_ACCEPT_ATTR =
  "image/*,image/jpeg,image/png,image/webp,application/pdf,.jpg,.jpeg,.png,.webp,.pdf";
export const RECEIPT_ACCEPT_IMAGE =
  "image/*,image/jpeg,image/png,image/webp,.jpg,.jpeg,.png,.webp";
export const RECEIPT_ACCEPT_PDF = "application/pdf,.pdf";

const MAX_RECEIPT_BYTES = 10 * 1024 * 1024;
const RECEIPT_MIME = new Set(["image/jpeg", "image/png", "image/webp", "application/pdf"]);

function isAllowedReceiptFile(file: File): boolean {
  if (RECEIPT_MIME.has(file.type)) return true;
  if (!file.type || file.type === "application/octet-stream") {
    return /\.(jpe?g|png|webp|pdf)$/i.test(file.name);
  }
  return /\.pdf$/i.test(file.name);
}

export type PaymentConfirmationVariant = "sheet" | "inline";

export interface PaymentConfirmationSheetProps {
  lang: StoreV2Lang;
  open: boolean;
  orderNo: string;
  amountLabel: string;
  paymentComment: string;
  receiptPreview: string | null;
  receiptFileName: string | null;
  /** Bytes — shown for PDF; also helps enable Submit after pick. */
  receiptFileSize?: number | null;
  busy: boolean;
  uploadProgress?: "idle" | "uploading" | "retrying";
  /** Default sheet keeps overlay UX; inline expands in-panel without covering viewport. */
  variant?: PaymentConfirmationVariant;
  onClose: () => void;
  onCommentChange: (value: string) => void;
  onReceiptSelected: (file: File | null) => void;
  onSubmit: () => void;
}

function formatFileSize(bytes: number, en: boolean): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} ${en ? "KB" : "КБ"}`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} ${en ? "MB" : "МБ"}`;
}

export function PaymentConfirmationSheet({
  lang,
  open,
  orderNo,
  amountLabel,
  paymentComment,
  receiptPreview,
  receiptFileName,
  receiptFileSize = null,
  busy,
  uploadProgress = "idle",
  variant = "sheet",
  onClose,
  onCommentChange,
  onReceiptSelected,
  onSubmit,
}: PaymentConfirmationSheetProps) {
  const imageInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const pdfInputRef = useRef<HTMLInputElement>(null);
  const titleId = useId();
  const pickGuard = useRef(false);
  const [fullPreview, setFullPreview] = useState(false);
  /** Local meta so Submit enables immediately on pick (parent state can lag one frame). */
  const [localName, setLocalName] = useState<string | null>(null);
  const [localSize, setLocalSize] = useState<number>(0);
  const [pickError, setPickError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      pickGuard.current = false;
      setFullPreview(false);
      setLocalName(null);
      setLocalSize(0);
      setPickError(null);
      return;
    }
    if (receiptFileName) {
      setLocalName(receiptFileName);
      setLocalSize(typeof receiptFileSize === "number" ? receiptFileSize : 0);
    } else {
      setLocalName(null);
      setLocalSize(0);
    }
  }, [open, receiptFileName, receiptFileSize]);

  if (!open) return null;

  const validation = validatePaymentComment(paymentComment);
  const en = lang === "en";
  const title = en ? "Attach receipt" : "Прикріпити квитанцію";
  const submitLabel = en ? "Send receipt" : "Надіслати квитанцію";
  const closeLabel = en ? "Cancel" : "Скасувати";
  const replaceLabel = en ? "Replace" : "Замінити";
  const removeLabel = en ? "Remove" : "Прибрати";
  const displayName = localName ?? receiptFileName;
  const displaySize = localSize || (typeof receiptFileSize === "number" ? receiptFileSize : 0);
  const hasValidFile = Boolean(displayName && String(displayName).trim());
  const isPdf =
    Boolean(displayName) &&
    !receiptPreview &&
    (/\.pdf$/i.test(String(displayName)) || displayName === "receipt.pdf");
  const sizeLabel = formatFileSize(displaySize, en);

  const openPicker = (kind: "image" | "camera" | "pdf") => {
    if (busy || pickGuard.current) return;
    pickGuard.current = true;
    setPickError(null);
    emitEvent("payment_receipt_picker_opened", { picker_kind: kind });
    const input =
      kind === "pdf" ? pdfInputRef.current : kind === "camera" ? cameraInputRef.current : imageInputRef.current;
    if (input) {
      input.value = "";
      input.click();
    }
    window.setTimeout(() => {
      pickGuard.current = false;
    }, 800);
  };

  const applyFile = (file: File | null) => {
    // Cancel-safe: empty FileList from WebView cancel must not wipe an existing pick.
    if (!file) return;
    if (!file.size) {
      setPickError(en ? "Empty file is not allowed." : "Порожній файл не дозволено.");
      return;
    }
    if (!isAllowedReceiptFile(file)) {
      setPickError(en ? "Only JPEG, PNG, WebP or PDF are allowed." : "Дозволені JPEG, PNG, WebP або PDF.");
      return;
    }
    if (file.size > MAX_RECEIPT_BYTES) {
      setPickError(en ? "File is too large. Max 10 MB." : "Файл занадто великий. Максимум 10 МБ.");
      return;
    }
    setPickError(null);
    setLocalName(file.name);
    setLocalSize(file.size);
    onReceiptSelected(file);
  };

  const clearFile = () => {
    setLocalName(null);
    setLocalSize(0);
    setPickError(null);
    onReceiptSelected(null);
  };

  const handleCancel = () => {
    if (busy) return;
    setLocalName(null);
    setLocalSize(0);
    setPickError(null);
    onReceiptSelected(null);
    onClose();
  };

  const progressLabel =
    uploadProgress === "uploading"
      ? en
        ? "Checking the receipt…"
        : "Перевіряємо квитанцію…"
      : uploadProgress === "retrying"
        ? en
          ? "Retrying…"
          : "Повтор…"
        : null;

  const fileInputs = (
    <>
      {/* Gallery / files — no capture (Telegram Android WebView gallery path). */}
      <input
        ref={imageInputRef}
        type="file"
        accept={RECEIPT_ACCEPT_IMAGE}
        className="sv2-visually-hidden"
        data-testid="sv2-receipt-file-input-image"
        tabIndex={-1}
        aria-hidden="true"
        onChange={(event) => {
          const next = event.target.files?.[0] ?? null;
          if (next) applyFile(next);
        }}
      />
      {/* Photo path only — capture forces camera; do not put capture on gallery/PDF. */}
      <input
        ref={cameraInputRef}
        type="file"
        accept={RECEIPT_ACCEPT_IMAGE}
        capture="environment"
        className="sv2-visually-hidden"
        data-testid="sv2-receipt-file-input-camera"
        tabIndex={-1}
        aria-hidden="true"
        onChange={(event) => {
          const next = event.target.files?.[0] ?? null;
          if (next) applyFile(next);
        }}
      />
      <input
        ref={pdfInputRef}
        type="file"
        accept={RECEIPT_ACCEPT_PDF}
        className="sv2-visually-hidden"
        data-testid="sv2-receipt-file-input-pdf"
        tabIndex={-1}
        aria-hidden="true"
        onChange={(event) => {
          const next = event.target.files?.[0] ?? null;
          if (next) applyFile(next);
        }}
      />
      {/* Keep legacy test id on gallery input for older guards. */}
      <input
        type="file"
        accept={RECEIPT_ACCEPT_ATTR}
        className="sv2-visually-hidden"
        data-testid="sv2-receipt-file-input"
        tabIndex={-1}
        aria-hidden="true"
        onChange={(event) => {
          const next = event.target.files?.[0] ?? null;
          if (next) applyFile(next);
        }}
      />
    </>
  );

  const body = (
    <>
      <div className="sv2-section-title" id={titleId}>
        {title}
      </div>
      <div className="sv2-muted sv2-receipt-meta">
        {en ? "Order" : "Замовлення"} {orderNo}
        <span> · {amountLabel}</span>
      </div>

      {fileInputs}

      {displayName || receiptPreview ? (
        <div className="sv2-receipt-preview" data-testid="sv2-receipt-preview">
          {receiptPreview ? (
            <button type="button" className="sv2-receipt-thumb-btn" onClick={() => setFullPreview(true)}>
              <img src={receiptPreview} alt={displayName ?? "receipt"} className="sv2-receipt-thumb" />
            </button>
          ) : (
            <div className="sv2-receipt-pdf" data-testid="sv2-receipt-pdf">
              <span aria-hidden="true">📄</span>
              <div>
                <div className="sv2-receipt-filename">{displayName ?? (en ? "PDF file" : "PDF-файл")}</div>
                {sizeLabel ? <div className="sv2-muted sv2-receipt-filesize">{sizeLabel}</div> : null}
              </div>
            </div>
          )}
          {receiptPreview && displayName ? (
            <div className="sv2-receipt-filename">{displayName}</div>
          ) : null}
          <div className="sv2-receipt-inline-actions">
            <button
              type="button"
              className="sv2-cta sv2-secondary sv2-receipt-mini"
              disabled={busy}
              onClick={() => openPicker(isPdf ? "pdf" : "image")}
            >
              {replaceLabel}
            </button>
            <button
              type="button"
              className="sv2-cta sv2-secondary sv2-receipt-mini"
              disabled={busy}
              onClick={clearFile}
            >
              {removeLabel}
            </button>
          </div>
        </div>
      ) : (
        <div className="sv2-receipt-pickers" data-testid="sv2-receipt-picker-btn">
          <button type="button" className="sv2-receipt-drop" disabled={busy} onClick={() => openPicker("image")}>
            {en ? "Photo / screenshot" : "Фото / скріншот"}
            <span className="sv2-muted">JPEG, PNG, WebP</span>
          </button>
          <button type="button" className="sv2-receipt-drop" disabled={busy} onClick={() => openPicker("camera")}>
            {en ? "Take photo" : "Зробити фото"}
            <span className="sv2-muted">
              {en ? "Live camera when available" : "Жива камера, якщо доступна"}
            </span>
          </button>
          <button type="button" className="sv2-receipt-drop" disabled={busy} onClick={() => openPicker("pdf")}>
            {en ? "PDF / file" : "PDF / файл"}
            <span className="sv2-muted">PDF, до 10 МБ</span>
          </button>
        </div>
      )}

      {pickError ? (
        <div className="sv2-alert sv2-alert-err" data-testid="sv2-receipt-pick-error" role="alert">
          {pickError}
        </div>
      ) : null}

      <label className="sv2-field sv2-receipt-comment">
        <span>{en ? "Comment (optional)" : "Коментар (необов’язково)"}</span>
        <textarea
          value={paymentComment}
          maxLength={500}
          rows={2}
          disabled={busy}
          onChange={(event) => onCommentChange(event.target.value)}
          placeholder={en ? "For example: paid from another account" : "Наприклад: оплата з іншого рахунку"}
        />
      </label>
      {!validation.ok ? <div className="sv2-alert sv2-alert-err">{validation.message}</div> : null}
      {progressLabel ? (
        <div className="sv2-muted" data-testid="sv2-receipt-progress">
          {progressLabel}
        </div>
      ) : null}
    </>
  );

  const actions = (
    <div className={variant === "inline" ? "sv2-receipt-inline-sticky" : "sv2-receipt-sticky"}>
      <button
        type="button"
        className="sv2-cta"
        data-testid="sv2-receipt-submit"
        disabled={busy || !validation.ok || !hasValidFile}
        onClick={onSubmit}
      >
        {busy ? (en ? "Sending…" : "Надсилаємо…") : submitLabel}
      </button>
      <button
        type="button"
        className="sv2-receipt-cancel"
        data-testid="sv2-receipt-cancel"
        disabled={busy}
        onClick={handleCancel}
      >
        {closeLabel}
      </button>
    </div>
  );

  const fullPreviewOverlay =
    fullPreview && receiptPreview ? (
      <button type="button" className="sv2-receipt-full" onClick={() => setFullPreview(false)}>
        <img src={receiptPreview} alt="" />
      </button>
    ) : null;

  if (variant === "inline") {
    return (
      <div
        className="sv2-info-card sv2-receipt-inline"
        data-testid="sv2-receipt-inline"
        role="region"
        aria-labelledby={titleId}
      >
        <div className="sv2-receipt-inline-body">{body}</div>
        {actions}
        {fullPreviewOverlay}
      </div>
    );
  }

  return (
    <div className="sv2-sheet-overlay" role="dialog" aria-modal="true" aria-labelledby={titleId}>
      <button type="button" className="sv2-sheet-backdrop" aria-label={closeLabel} onClick={handleCancel} disabled={busy} />
      <div className="sv2-sheet-panel sv2-receipt-sheet">
        <div className="sv2-sheet-handle" aria-hidden="true" />
        <div className="sv2-receipt-sheet-body">{body}</div>
        {actions}
      </div>
      {fullPreviewOverlay}
    </div>
  );
}
